/**
 * Form 8233 Models
 * Type-safe models for Form 8233 tax form data handling, validation, and XML export
 */

import { parseAddressComponents } from "../utils/formUtils";

// API Response Model - Complete form fields from API
export class Form8233ResponseModel {
  // Dynamic fields support
  [key: string]: any;
  // Core Form Fields
  "Form_name": string = "Form 8233";
  "Form_revision_date": string = "2021";
  "Form-8233-Beginning_year": string = "";
  "Form-8233-Calendar_year": string = "";
  "Form-8233-Ending_year": string = "";
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";

  // Part 1 - Individual Information
  "Form-8233-Part_1_individual_name": string = "";
  "Form-8233-Part_1_us_tax_id": string = "";
  "Form-8233-Part_1_ftin": string = "";

  // Part 1 - Address Information
  "Form-8233-Part_1_permanent_address_street": string = "";
  "Form-8233-Part_1_permanent_address_city_state_postal": string = "";
  "Form-8233-Part_1_permanent_address_country": string = "";
  "Form-8233-Part_1_us_address_street": string = "";
  "Form-8233-Part_1_us_address_city_state_zip": string = "";

  // Permanent Address fields (standardized)

  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address other": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";
  "Permanent city address": string = "";

  // US Address fields
  "US address line 2": string = "";
  "US address line 3": string = "";
  "US address city": string = "";
  "US address state USCA": string = "";
  "US address zip": string = "";
  "US address other": string = "";
  "US address is care of": string = "no";
  "US address is po box": string = "no";
  "US address is rural route": string = "no";

  // Part 1 - Immigration Information
  "Form-8233-Part_1_us_visa_type": string = "";
  "Form-8233-Part_1_passport_country": string = "";
  "Form-8233-Part_1_passport_number": string = "";
  "Form-8233-Part_1_date_entry_to_us": string = "";
  "Form-8233-Part_1_current_nonimmigrant_status": string = "";
  "Form-8233-Part_1_current_nonimmigrant_status_expires": string = "";
  "Form-8233-Part_1_foreign_student_trainee_researcher": string = "";

  // Part 2 - Personal Services
  "Form-8233-Part_2_personal_services": string = "";
  "Form-8233-Part_2_total_compensation_expected": string = "";
  "Form-8233-Part_2_tax_treaty": string = "";
  "Form-8233-Part_2_treaty_article": string = "";
  "Form-8233-Part_2_total_compensation_listed_tax_under_treaty": string = "";
  "Form-8233-Part_2_country_residence": string = "";
  "Form-8233-Part_2_sufficient_facts": string = "";
  "Form-8233-Part_2_scholarship_amount": string = "";

  // Part 3 - Scholarship/Fellowship
  "Form-8233-Part_3_scholarship_tax_treaty": string = "";
  "Form-8233-Part_3_scholarship_treaty_article": string = "";
  "Form-8233-Part_3_scholarship_total_income_under_treaty": string = "";
  "Form-8233-Part_3_signature": string = "";
  "Form-8233-Part_3_signature_date": string = "";

  // Part 4 - Withholding Agent Information
  "Form-8233-Part_4_withholding_name": string = "";
  "Form-8233-Part_4_eid": string = "";
  "Form-8233-Part_4_address_street": string = "";
  "Form-8233-Part_4_address_city_state_zip": string = "";
  "Form-8233-Part_4_telephone_number": string = "";
  "Form-8233-Part_4_signature": string = "";
  "Form-8233-Part_4_signature_date": string = "";
  "Signature_unreadable": boolean = false;

  "isSigned": string = "no";
  "isModified": string = "no";

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

/**
 * Form8233 Field Mapper - Maps response model fields to form model fields
 * Following the same pattern as W8BEN and W9 models
 */
export class Form8233FieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: Form8233ResponseModel): Form8233Model {
    const formModel = new Form8233Model();

    // Check if mapping is already done to prevent re-mapping
    if (formModel.isMapDone) {
      return formModel;
    }

    // Direct field mappings (fields that exist in both models)
    const directMappings = [
      "UniqueIdentifier",
      "entityUniqueIdentifier",
      "Form_name",
      "Form_revision_date",
      "Form-8233-Beginning_year",
      "Form-8233-Calendar_year",
      "Form-8233-Ending_year",

      // Part 1 - Individual Information
      "Form-8233-Part_1_individual_name",
      "Form-8233-Part_1_us_tax_id",
      "Form-8233-Part_1_ftin",

      // Part 1 - Address Information (direct API fields)
      "Form-8233-Part_1_permanent_address_street",
      "Form-8233-Part_1_permanent_address_city_state_postal",
      "Form-8233-Part_1_permanent_address_country",
      "Form-8233-Part_1_us_address_street",
      "Form-8233-Part_1_us_address_city_state_zip",

      // Part 1 - Immigration Information
      "Form-8233-Part_1_us_visa_type",
      "Form-8233-Part_1_passport_country",
      "Form-8233-Part_1_passport_number",
      "Form-8233-Part_1_date_entry_to_us",
      "Form-8233-Part_1_current_nonimmigrant_status",
      "Form-8233-Part_1_current_nonimmigrant_status_expires",
      "Form-8233-Part_1_foreign_student_trainee_researcher",

      // Part 2 - Personal Services
      "Form-8233-Part_2_personal_services",
      "Form-8233-Part_2_total_compensation_expected",
      "Form-8233-Part_2_tax_treaty",
      "Form-8233-Part_2_treaty_article",
      "Form-8233-Part_2_total_compensation_listed_tax_under_treaty",
      "Form-8233-Part_2_country_residence",
      "Form-8233-Part_2_sufficient_facts",

      // Part 3 - Scholarship/Fellowship
      "Form-8233-Part_2_scholarship_amount",
      "Form-8233-Part_3_scholarship_tax_treaty",
      "Form-8233-Part_3_scholarship_treaty_article",
      "Form-8233-Part_3_scholarship_total_income_under_treaty",
      "Form-8233-Part_3_signature",
      "Form-8233-Part_3_signature_date",

      // Part 4 - Withholding Agent Information
      "Form-8233-Part_4_withholding_name",
      "Form-8233-Part_4_eid",
      "Form-8233-Part_4_address_street",
      "Form-8233-Part_4_address_city_state_zip",
      "Form-8233-Part_4_telephone_number",
      "Form-8233-Part_4_signature",
      "Form-8233-Part_4_signature_date",
      "Signature_unreadable",
      "isSigned",
      "isModified",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Handle dynamic fields from response
    Object.keys(responseModel).forEach((key) => {
      if (!directMappings.includes(key) && responseModel[key] !== undefined) {
        formModel[key] = responseModel[key];
      }
    });

    // Set isSigned based on signature value if not already set
    // Only do this when isMapDone is false (initial mapping from API)
    if (!formModel.isMapDone) {
      const signatureField =
        formModel["Form-8233-Part_4_signature"] ||
        formModel["Form-8233-Part_3_signature"] ||
        formModel["Signature"];

      if (signatureField && typeof signatureField === "string") {
        const hasSignature = signatureField.trim() !== "";
        formModel["isSigned"] = hasSignature ? "yes" : "no";
        if (hasSignature) {
          formModel["Signature_unreadable"] = true;
        }
      }
    }

    // Set mapping done flag to prevent re-mapping
    formModel.isMapDone = true;

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: Form8233ResponseModel,
    formModel: Form8233Model
  ): void {
    // Map API fields (hyphen format) to form fields (underscore format)

    if (responseModel["Form-8233-Part_1_us_address_city_state_zip"]) {
      // Parse address components for US mailing address
      const addressComponents = parseAddressComponents(
        responseModel["Form-8233-Part_1_us_address_city_state_zip"]
      );
      formModel["US address city"] = addressComponents.city;
      formModel["US address state USCA"] = addressComponents.state;
      formModel["US address zip"] = addressComponents.postalCode;
    }

    // Also parse if "US address city" comes directly with combined data (like "Madison WI 53706")
    if (
      responseModel["US address city"] &&
      (responseModel["US address city"].includes(" ") ||
        /\d{5}/.test(responseModel["US address city"]))
    ) {
      const addressComponents = parseAddressComponents(responseModel["US address city"]);
      if (addressComponents.city && (addressComponents.state || addressComponents.postalCode)) {
        console.log(
          `🔍 Form8233 - Parsing US address city field directly: "${responseModel["US address city"]}"`
        );
        console.log(`🔍 Form8233 - Parsed to:`, addressComponents);
        formModel["US address city"] = addressComponents.city;
        if (addressComponents.state) formModel["US address state USCA"] = addressComponents.state;
        if (addressComponents.postalCode)
          formModel["US address zip"] = addressComponents.postalCode;
      }
    }

    // Handle API fields with underscores/dashes - map to form fields with spaces
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined) {
          (formModel as any)[responseField] = value;
        }

        // Handle API fields with underscores/dashes - map to form fields with spaces
        const formFieldName = responseField.replace(/_/g, " ").replace(/-/g, " ");
        if (formFieldName !== responseField && (formModel as any)[formFieldName] !== undefined) {
          console.log(
            `Form8233 mapping API field "${responseField}" to form field "${formFieldName}"`
          );
          (formModel as any)[formFieldName] = value;
        }
      }
    });
  }
}

export interface Form8233Data {
  // Core Form Fields
  UniqueIdentifier: string;
  Form_name: string;
  Form_revision_date: string;
  "Form-8233-Beginning_year": string;
  "Form-8233-Calendar_year": string;
  "Form-8233-Ending_year": string;

  // Part 1 - Individual Information
  "Form-8233-Part_1_individual_name": string;
  "Form-8233-Part_1_us_tax_id": string;
  "Form-8233-Part_1_ftin": string;

  // Part 1 - Address Information
  "Form-8233-Part_1_permanent_address_street": string;
  "Form-8233-Part_1_permanent_address_city_state_postal": string;
  "Form-8233-Part_1_permanent_address_country": string;
  "Form-8233-Part_1_us_address_street": string;
  "Form-8233-Part_1_us_address_city_state_zip": string;

  // Part 1 - Immigration Information
  "Form-8233-Part_1_us_visa_type": string;
  "Form-8233-Part_1_passport_country": string;
  "Form-8233-Part_1_passport_number": string;
  "Form-8233-Part_1_date_entry_to_us": string;
  "Form-8233-Part_1_current_nonimmigrant_status": string;
  "Form-8233-Part_1_current_nonimmigrant_status_expires": string;
  "Form-8233-Part_1_foreign_student_trainee_researcher": string;

  // Part 2 - Personal Services
  "Form-8233-Part_2_personal_services": string;
  "Form-8233-Part_2_total_compensation_expected": string;
  "Form-8233-Part_2_tax_treaty": string;
  "Form-8233-Part_2_treaty_article": string;
  "Form-8233-Part_2_total_compensation_listed_tax_under_treaty": string;
  "Form-8233-Part_2_country_residence": string;
  "Form-8233-Part_2_sufficient_facts": string;

  // Part 3 - Scholarship/Fellowship
  "Form-8233-Part_2_scholarship_amount": string;
  "Form-8233-Part_3_scholarship_tax_treaty": string;
  "Form-8233-Part_3_scholarship_treaty_article": string;
  "Form-8233-Part_3_scholarship_total_income_under_treaty": string;
  "Form-8233-Part_3_signature": string;
  "Form-8233-Part_3_signature_date": string;

  // Part 4 - Withholding Agent Information
  "Form-8233-Part_4_withholding_name": string;
  "Form-8233-Part_4_eid": string;
  "Form-8233-Part_4_address_street": string;
  "Form-8233-Part_4_address_city_state_zip": string;
  "Form-8233-Part_4_telephone_number": string;
  "Form-8233-Part_4_signature": string;
  "Form-8233-Part_4_signature_date": string;

  isSigned: string;
  isModified: string;

  // Dynamic field support
  [key: string]: string | boolean;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string>;
}

export class Form8233Model implements Form8233Data {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "";
  "Form_revision_date": string = "";
  "Form-8233-Beginning_year": string = "";
  "Form-8233-Calendar_year": string = "";
  "Form-8233-Ending_year": string = "";
  "UniqueIdentifier": string = "";
  "entityUniqueIdentifier": string = "";

  // Part 1 - Individual Information
  "Form-8233-Part_1_individual_name": string = "";
  "Form-8233-Part_1_us_tax_id": string = "";
  "Form-8233-Part_1_ftin": string = "";

  // Part 1 - Address Information (original fields)
  "Form-8233-Part_1_permanent_address_street": string = "";
  "Form-8233-Part_1_permanent_address_city_state_postal": string = "";
  "Form-8233-Part_1_permanent_address_country": string = "";
  "Form-8233-Part_1_us_address_street": string = "";
  "Form-8233-Part_1_us_address_city_state_zip": string = "";

  // Permanent Address fields (standardized API compatibility)
  "Permanent residence address": string = "";
  "Permanent address line 2": string = "";
  "Permanent address line 3": string = "";
  "Permanent address city": string = "";
  "Permanent address state USCA": string = "";
  "Permanent address state NonUS": string = "";
  "Permanent address zip": string = "";
  "Permanent address other": string = "";
  "Permanent address is care of": string = "no";
  "Permanent address is po box": string = "no";
  "Permanent address is rural route": string = "no";
  "Permanent city address": string = "";

  "US address line 2": string = "";
  "US address line 3": string = "";
  "US address city": string = "";
  "US address state USCA": string = "";
  "US address zip": string = "";
  "US address country": string = "";
  "US address other": string = "";
  "US address is care of": string = "no";
  "US address is po box": string = "no";
  "US address is rural route": string = "no";

  // Part 1 - Immigration Information
  "Form-8233-Part_1_us_visa_type": string = "";
  "Form-8233-Part_1_passport_country": string = "";
  "Form-8233-Part_1_passport_number": string = "";
  "Form-8233-Part_1_date_entry_to_us": string = "";
  "Form-8233-Part_1_current_nonimmigrant_status": string = "";
  "Form-8233-Part_1_current_nonimmigrant_status_expires": string = "";
  "Form-8233-Part_1_foreign_student_trainee_researcher": string = "";

  // Part 2 - Personal Services
  "Form-8233-Part_2_personal_services": string = "";
  "Form-8233-Part_2_total_compensation_expected": string = "";
  "Form-8233-Part_2_tax_treaty": string = "";
  "Form-8233-Part_2_treaty_article": string = "";
  "Form-8233-Part_2_total_compensation_listed_tax_under_treaty": string = "";
  "Form-8233-Part_2_country_residence": string = "";
  "Form-8233-Part_2_sufficient_facts": string = "";

  // Part 3 - Scholarship/Fellowship
  "Form-8233-Part_2_scholarship_amount": string = "";
  "Form-8233-Part_3_scholarship_tax_treaty": string = "";
  "Form-8233-Part_3_scholarship_treaty_article": string = "";
  "Form-8233-Part_3_scholarship_total_income_under_treaty": string = "";
  "Form-8233-Part_3_signature": string = "";
  "Form-8233-Part_3_signature_date": string = "";

  // Part 4 - Withholding Agent Information
  "Form-8233-Part_4_withholding_name": string = "";
  "Form-8233-Part_4_eid": string = "";
  "Form-8233-Part_4_address_street": string = "";
  "Form-8233-Part_4_address_city_state_zip": string = "";
  "Form-8233-Part_4_telephone_number": string = "";
  "Form-8233-Part_4_signature": string = "";
  "Form-8233-Part_4_signature_date": string = "";
  "Signature_unreadable": boolean = false;

  "isSigned": string = "no";
  "isModified": string = "no";

  // Mapping control field - prevents re-mapping when already done
  isMapDone: boolean = false;

  constructor(data?: Partial<Form8233Data>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Validate the form data
   */
  validate(): ValidationResult {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Required field validations (consistent with other forms)
    const requiredFields = ["UniqueIdentifier", "Form-8233-Part_1_individual_name"];

    requiredFields.forEach((field) => {
      if (!this[field]) {
        const error = `${field.replace(/[-_]/g, " ")} is required`;
        errors.push(error);
        fieldErrors[field] = error;
      }
    });

    // Certification validation removed per user request

    // Date validations
    if (
      this["Form-8233-Part_1_date_entry_to_us"] &&
      !this.isValidDate(this["Form-8233-Part_1_date_entry_to_us"])
    ) {
      const error = "Date of entry to U.S. must be a valid date";
      errors.push(error);
      fieldErrors["Form-8233-Part_1_date_entry_to_us"] = error;
    }

    if (
      this["Form-8233-Part_1_current_nonimmigrant_status_expires"] &&
      !this.isValidDate(this["Form-8233-Part_1_current_nonimmigrant_status_expires"])
    ) {
      const error = "Nonimmigrant status expiration date must be a valid date";
      errors.push(error);
      fieldErrors["Form-8233-Part_1_current_nonimmigrant_status_expires"] = error;
    }

    if (
      this["Form-8233-Part_3_signature_date"] &&
      !this.isValidDate(this["Form-8233-Part_3_signature_date"])
    ) {
      const error = "Part III signature date must be a valid date";
      errors.push(error);
      fieldErrors["Form-8233-Part_3_signature_date"] = error;
    }

    if (
      this["Form-8233-Part_4_signature_date"] &&
      !this.isValidDate(this["Form-8233-Part_4_signature_date"])
    ) {
      const error = "Part IV signature date must be a valid date";
      errors.push(error);
      fieldErrors["Form-8233-Part_4_signature_date"] = error;
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors,
    };
  }

  /**
   * Check if a date string is valid
   */
  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Generate XML representation using the Form 8233 XML format
   */
  async toXML(): Promise<string> {
    // Import the XML generator
    const { generateForm8233XML } = await import(
      "../components/SendingForms/utils/generateForm8233XML"
    );

    // Map model data to the format expected by XML generator
    const xmlData = {
      // Use the model data directly - the XML generator will handle field mapping
      ...this,

      // Ensure key fields are properly mapped
      UniqueIdentifier: this["UniqueIdentifier"] || "",
      Instructor_Identifier: this["UniqueIdentifier"] || "",
      Entity: this["UniqueIdentifier"] || "",

      // Individual Information
      FirstName: this["Form-8233-Part_1_individual_name"]?.split(" ")[0] || "",
      LastName: this["Form-8233-Part_1_individual_name"]?.split(" ").slice(1).join(" ") || "",
      CountryOfCitizenshipForTax: this["Form-8233-Part_2_country_residence"] || "",

      // Taxpayer Identification
      SSNOrITIN: this["Form-8233-Part_1_us_tax_id"] || "",
      ForeignTaxpayerId: this["Form-8233-Part_1_ftin"] || "",
      ForeignTaxpayerCountry: this["Form-8233-Part_2_country_residence"] || "",

      // Passport Information
      CountryIssuingPassport: this["Form-8233-Part_1_passport_country"] || "",
      PassportNumber: this["Form-8233-Part_1_passport_number"] || "",
      USEntryDate: this["Form-8233-Part_1_date_entry_to_us"] || "",
      NonimmigrantStatus: this["Form-8233-Part_1_current_nonimmigrant_status"] || "",
      NonimmigrantStatusExpiryDate:
        this["Form-8233-Part_1_current_nonimmigrant_status_expires"] || "",

      // Form Specific Fields
      PersonalServicesValue: this["Form-8233-Part_2_total_compensation_expected"] || "",
      PartIILine12bExemptTotal:
        this["Form-8233-Part_2_total_compensation_listed_tax_under_treaty"] || "",
      PartIILine13cExemptTotal:
        this["Form-8233-Part_3_scholarship_total_income_under_treaty"] || "",
      CalendarYear: this["Form-8233-Calendar_year"] || new Date().getFullYear(),

      // Contact Information
      TelephoneNumber: this["Form-8233-Part_4_telephone_number"] || "",
      TelephoneCountry: this["Form-8233-Part_1_permanent_address_country"] || "",

      // Signature Information
      IsSigned: this["isSigned"] === "yes" || this["isSigned"] === "true",
      SignedBy: this["Form-8233-Part_3_signature"],
      SignDate: this["Form-8233-Part_3_signature_date"] || "",
    };

    return await generateForm8233XML(xmlData);
  }
}
